import React, { useCallback, useEffect, useState } from 'react';
import { Box, CircularProgress, Typography, Paper } from '@mui/material';
import AdminLogin from './AdminLogin';
import AdminDashboard from './AdminDashboard';

const apiUrl = process.env.REACT_APP_API_URL;

type SessionState = 'loading' | 'not-configured' | 'anonymous' | 'authenticated';

// Gate for the whole /admin area: checks the session cookie once on load and
// swaps between the login form and the dashboard. All admin fetches need
// `credentials: 'include'` — the API lives on a different origin in dev
// (and often in prod), so cookies aren't sent by default.
const AdminApp = () => {
  const [state, setState] = useState<SessionState>('loading');

  const checkSession = useCallback(() => {
    fetch(`${apiUrl}/api/admin/session`, { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        if (!data.configured) setState('not-configured');
        else setState(data.authenticated ? 'authenticated' : 'anonymous');
      })
      .catch(() => setState('anonymous'));
  }, []);

  useEffect(() => { checkSession(); }, [checkSession]);

  if (state === 'loading') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', padding: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (state === 'not-configured') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
        <Paper sx={{ padding: 3, maxWidth: 480 }}>
          <Typography variant="h6" gutterBottom>Admin not configured</Typography>
          <Typography variant="body2" color="text.secondary">
            Set <code>ADMIN_PASSWORD</code> in the server's environment (see <code>.env.example</code>) to enable this page.
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (state === 'anonymous') {
    return <AdminLogin onLoggedIn={() => setState('authenticated')} />;
  }

  return <AdminDashboard onLoggedOut={() => setState('anonymous')} />;
};

export default AdminApp;
